import { FC, cloneElement, isValidElement } from 'react';

import {
  Box,
  Image,
  Popover,
  PopoverProps,
  Stack,
  Text,
  TextProps,
  useDisclosure
} from '@ui';
interface ExplainerPopoverProps extends PopoverProps {
  title?: string;
  subTitle?: string;
  titleProps?: TextProps;
  subTitleProps?: TextProps;
  image?: React.ReactNode;
  icon?: React.ReactNode;
  target: React.ReactNode;
  relative?: boolean;
}

import classes from './styles/ExplainerPopover.module.css';

const ExplainerPopover: FC<ExplainerPopoverProps> = ({
  target,
  title,
  titleProps,
  subTitle,
  subTitleProps,
  image,
  icon,
  relative = false,
  children,
  ...rest
}) => {
  const [opened, { close, open }] = useDisclosure(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    opened ? close() : open();
  };

  // if no title, subtitle, or icon is provided, return the target without a popover
  if (!children && !title && !subTitle && !icon) {
    return <>{target}</>;
  }

  return (
    <Popover
      width={230}
      withArrow
      position="top"
      arrowSize={20}
      classNames={{
        dropdown: classes.dropdown,
        arrow: classes.arrow
      }}
      withinPortal
      {...rest}
      opened={opened}
    >
      <Popover.Target>
        {!relative ? (
          isValidElement(target) ? (
            cloneElement(target as React.ReactElement<any>, {
              onMouseEnter: open,
              onMouseLeave: close,
              onClick: handleClick
            })
          ) : (
            target
          )
        ) : (
          <Box
            onMouseEnter={open}
            onMouseLeave={close}
            onClick={handleClick}
            className={classes.relativeTarget}
          >
            {target}
          </Box>
        )}
      </Popover.Target>
      <Popover.Dropdown>
        {children
          ? children
          : (title || subTitle) && (
              <Stack gap="xs">
                {image && <Image src={image} w={32} h={32} mb="xs" />}
                {icon && icon}
                {title && (
                  <Text fz={16} fw={600} {...titleProps}>
                    {title}
                  </Text>
                )}
                {subTitle && (
                  <Text fz={14} c="secondary" {...subTitleProps}>
                    {subTitle}
                  </Text>
                )}
              </Stack>
            )}
      </Popover.Dropdown>
    </Popover>
  );
};

export { ExplainerPopover };
